/**
 * Firebase Auth error-code → i18n message-key mapping for the dashboard
 * sign-in + password-reset surfaces.
 *
 * Kept as PURE functions (no Firebase import, no React) so they unit-test
 * without an emulator and so the login component stays declarative. Callers
 * resolve the returned key through `useI18n().t(...)`, which honours rule #1
 * (no JSX string literals) and [[feedback-custom-crafted-ui-text]] — every
 * failure state gets a specific, human line, never a raw Firebase code and
 * never a dead "Error" ([[feedback-no-dead-ends]]).
 */

/**
 * Extract a Firebase `auth/*` error code from an unknown thrown value.
 * `FirebaseError` carries a string `code`; anything else yields `''` (→ the
 * generic bucket). No `any` / non-null assertions — a narrowing guard instead.
 */
export function firebaseAuthErrorCode(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = (err as { readonly code: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return '';
}

/**
 * Map a sign-in failure to a human i18n key under `auth.login.*`.
 *
 * `invalid-credential` / `wrong-password` / `user-not-found` all collapse to
 * the SAME "email or password is incorrect" line — telling the user WHICH of
 * the two was wrong would leak whether an account exists.
 */
export function loginErrorKey(err: unknown): string {
  switch (firebaseAuthErrorCode(err)) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-login-credentials':
      return 'auth.login.errInvalidCredential';
    case 'auth/invalid-email':
      return 'auth.login.errInvalidEmail';
    case 'auth/too-many-requests':
      return 'auth.login.errTooManyRequests';
    case 'auth/user-disabled':
      return 'auth.login.errUserDisabled';
    case 'auth/network-request-failed':
      return 'auth.login.errNetwork';
    default:
      return 'auth.login.errGeneric';
  }
}

/**
 * Map a "Continue with Google" failure to a human i18n key under
 * `auth.login.*`. Popup-cancel is a benign user action (return `null` → show
 * nothing); config errors (`operation-not-allowed` = provider not enabled,
 * `unauthorized-domain` = origin not allow-listed) get a distinct line so the
 * OWNER can tell a console-setup gap from a real failure.
 *
 * NOTE: `account-exists-with-different-credential` is NOT handled here — the
 * caller intercepts it BEFORE this to start the account-link flow.
 */
export function googleErrorKey(err: unknown): string | null {
  switch (firebaseAuthErrorCode(err)) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
    case 'auth/user-cancelled':
      return null; // the operator dismissed the popup — not an error to shout about
    case 'auth/popup-blocked':
      return 'auth.login.errPopupBlocked';
    case 'auth/operation-not-allowed':
    case 'auth/unauthorized-domain':
      return 'auth.login.errGoogleConfig';
    case 'auth/too-many-requests':
      return 'auth.login.errTooManyRequests';
    case 'auth/network-request-failed':
      return 'auth.login.errNetwork';
    default:
      return 'auth.login.errGoogleGeneric';
  }
}

/**
 * Map a password-reset failure to an i18n key — or `null` when the failure
 * MUST be treated as success to avoid leaking whether an email is registered
 * (`auth/user-not-found`). Callers show the neutral "check your inbox"
 * confirmation on `null`, so an attacker can't enumerate accounts through the
 * reset form.
 */
export function resetErrorKey(err: unknown): string | null {
  switch (firebaseAuthErrorCode(err)) {
    case 'auth/user-not-found':
      return null; // do NOT leak account existence — show the confirmation
    case 'auth/invalid-email':
      return 'auth.login.resetErrInvalidEmail';
    case 'auth/too-many-requests':
      return 'auth.login.resetErrTooManyRequests';
    case 'auth/network-request-failed':
      return 'auth.login.resetErrNetwork';
    default:
      return 'auth.login.resetErrGeneric';
  }
}
